import {
  Component,
  Event,
  ComponentInterface,
  EventEmitter,
  h,
  Prop,
  Element,
  getMode,
  State,
  Listen,
  Fragment
} from '@stencil/core';
import { onFocusWithTab, registerClickOutside } from '../../../../utils/utils';

@Component({
  tag: 'ds-action-menu',
  styleUrls: {
    default: 'ds-action-menu.default.scss',
    light: 'ds-action-menu.light.scss'
  },
  shadow: true
})
export class DsActionMenu implements ComponentInterface {
  private _actionMenuWrapper!: HTMLElement;
  private hasDropdownSlot: boolean;
  private isMouseDown = false;

  @Prop() open?: boolean = false;
  @Prop() mobile?: boolean = false;
  @Prop() openerTestId?: string;
  @Prop() openerLabel?: string;
  @Prop() openerTitle?: string;
  @Prop() dropdownTestId?: string;
  @Prop() href?: string;
  @Prop() openInNewTab?: boolean = false;

  @State() isFocused: boolean = false;

  @Event({ bubbles: false }) openerClick: EventEmitter<MouseEvent>;
  @Event({ bubbles: false }) clickOutside: EventEmitter<void>;

  @Element() hostElement: HTMLDsActionMenuElement;

  componentDidLoad(): void {
    registerClickOutside(this, this._actionMenuWrapper, () => {
      this.clickOutside.emit();
    });
  }

  componentWillRender() {
    this.hasDropdownSlot = !!this.hostElement.querySelector('[slot="dropdown-content"]');
  }

  @Listen('keydown', { passive: true })
  accessibilityHandler(e: KeyboardEvent) {
    if (e.key === 'Escape' && this.open && !this.mobile) {
      this.openerClick.emit();
      this.hostElement.shadowRoot.querySelector('button').focus();
      this.isFocused = true;
    }
  }

  private handleFocus = (e: FocusEvent) => {
    const isKeyboardFocus = e.detail === 0;
    const isProgramFocus = !e.relatedTarget;

    if (!this.isMouseDown && (isProgramFocus || isKeyboardFocus)) {
      this.isFocused = true;
    }

    onFocusWithTab(() => {
      this.isFocused = true;
    });
  };

  render() {
    const Opener = this.href ? 'A' : 'Button';
    return (
      <div
        class={{
          'action-menu': true,
          'action-menu--open': this.open,
          'action-menu--mobile': this.mobile
        }}
        ref={(el) => (this._actionMenuWrapper = el as HTMLElement)}
        data-mode={getMode(this)}
      >
        <Opener
          //Link
          href={this.href}
          target={this.openInNewTab ? '_blank' : null}
          //Button
          aria-expanded={this.href ? null : `${this.open}`}
          //Common
          class={{ 'action-menu__opener': true, 'action-menu__opener--focused': this.isFocused }}
          aria-label={this.openerLabel}
          title={this.openerTitle}
          data-testid={this.openerTestId}
          onClick={(event) => this.openerClick.emit(event)}
          onMouseDown={() => (this.isMouseDown = true)}
          onMouseUp={() => (this.isMouseDown = false)}
          onFocus={this.handleFocus}
          onFocusout={() => (this.isFocused = false)}
        >
          <slot name='opener-content' />
        </Opener>
        <div class='underline'></div>
        {this.hasDropdownSlot ? (
          <Fragment>
            <div
              class='action-menu__dropdown'
              part='dropdown'
              data-testid={this.dropdownTestId}
              tabIndex={-1}
            >
              <slot name='dropdown-content' />
            </div>
            {this.mobile ? (
              <div class='action-menu__dropdown action-menu__dropdown-shadow'></div>
            ) : null}
          </Fragment>
        ) : null}
      </div>
    );
  }
}
